import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Stack,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomLogo from "../../assets/icons/customLogo";
import BackIcon from "../../assets/icons/BackIcon.svg";
import { useCreateButton } from "../../hooks/useCreateButton";
import { useUserDetails } from "../../contexts/UserDetailsContext";
import useRBAC from "../../hooks/useRBAC";
import CsvExport from "../common/CsvExport";
import { RootState } from "../../store";
import { API_ENDPOINTS, DATE_FORMATS } from "../../constants";
import useCodeCommon from "../../hooks/useCodeCommon";
import { useCreateContext } from "../../contexts/CreateContext";
import apiService, {
  parseGetAllUsersResponse,
  parseSearchPaymentDetailsResponse,
} from "../../services/api";
import { convertTimestampToIST } from "../../utils/formatters";
import {
  getRoleHubRelations,
  getHubId,
  setHubId,
  setHubName,
} from "../../utils/cookieUtils";
import useRoleBasedHubFiltering from "../../hooks/useRoleBasedHubFiltering";
import { clearCreateBookingDraft } from "../../pages/booking-management/createBookingSessionStorage";
import { useHeaderAfterExportSlot } from "../../contexts/HeaderToolbarContext";

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUserName } = useUserDetails();
  const { user } = useSelector((state: RootState) => state.auth);
  const { canCreate, canExport } = useRBAC();
  const { isMultiHub, filterHubOptions } = useRoleBasedHubFiltering();
  const relations = getRoleHubRelations();
  const currentHubId = getHubId();
  const { setShowBulkUploadVehicle } = useCreateContext();
  const headerAfterExportSlot = useHeaderAfterExportSlot();

  /** Promo create page: any query string (e.g. `?id=1`) means edit mode for titles/labels. */
  const promoCodesCreateIsEdit =
    location.pathname === "/marketing/promo-codes/create" &&
    location.search.replace(/^\?/, "").trim().length > 0;

  // Get support status options for data transformation
  const { options: allSupportStatusOptions } = useCodeCommon(
    "SupportTicketStatus",
  );

  // Get trip status options for booking data transformation
  const { options: allTripStatusOptions } = useCodeCommon("TripStatus");

  // Get plan status options for plan data transformation
  const { options: allPlanStatusOptions } = useCodeCommon("PlanStatus");

  const { options: allVehicleStatusOptions } = useCodeCommon("VehicleStatus");
  const { options: vehicleTypeOptions } = useCodeCommon("VehicleType");
  const { options: evSpeedTypeOptions } = useCodeCommon("EVSpeedType");

  const [citiesMaster, setCitiesMaster] = useState<
    Array<{ city_id: number; city_name: string; state_id: number }>
  >([]);
  const [hubOptions, setHubOptions] = useState<
    Array<{ hubmasterid: number; hubname: string }>
  >([]);
  const [vehicleModelCodes, setVehicleModelCodes] = useState<
    Array<{ codevalue: string; codedisplayname: string }>
  >([]);

  const multiHubOptions = React.useMemo(() => {
    if (relations && relations.length > 1 && hubOptions.length > 0) {
      return filterHubOptions(hubOptions);
    }
    return [];
  }, [relations, hubOptions, filterHubOptions]);

  useEffect(() => {
    const fetchMasterCities = async () => {
      try {
        const response = await apiService.getCities();
        const data = (response as any)?.data || response;
        let list: any[] = [];
        if (Array.isArray(data)) list = data;
        else if (data && Array.isArray((data as any).result))
          list = (data as any).result;
        else if (data && Array.isArray((data as any).cities))
          list = (data as any).cities;
        setCitiesMaster(list);
      } catch {
        setCitiesMaster([]);
      }
    };
    fetchMasterCities();
  }, []);

  useEffect(() => {
    const fetchHubs = async () => {
      try {
        const response = await apiService.getAllHubs();
        const data = (response as any)?.data || response;
        let list: any[] = [];
        if (Array.isArray(data)) list = data;
        else if (data && Array.isArray(data.result)) list = data.result;
        setHubOptions(list);
      } catch {
        setHubOptions([]);
      }
    };
    fetchHubs();
  }, []);

  useEffect(() => {
    const fetchVehicleModels = async () => {
      try {
        const response: any =
          await apiService.getAllCodeCommonByCategory("VehicleModel");
        const raw = response?.data || response || [];
        const codes = Array.isArray(raw) ? raw : [];
        setVehicleModelCodes(codes);
      } catch {
        setVehicleModelCodes([]);
      }
    };
    fetchVehicleModels();
  }, []);

  const getItemHubId = (item: Record<string, unknown>): number | null => {
    const raw =
      item?.hubmasterid ?? item?.hubid ?? item?.hubMasterId ?? item?.hubId;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  /** API hub id for Export All (all accessible hubs). */
  const HUB_ID_EXPORT_ALL = 0;

  /** Selected header / cookie hub for scoped Export Excel/CSV. */
  const resolveHubMasterIdForScopedExport = (): number => {
    const hid = currentHubId ?? getHubId();
    return hid != null && Number.isFinite(Number(hid)) && Number(hid) > 0
      ? Number(hid)
      : HUB_ID_EXPORT_ALL;
  };

  const resolveHubIdForExportApi = (exportAll: boolean): number =>
    exportAll ? HUB_ID_EXPORT_ALL : resolveHubMasterIdForScopedExport();

  /** Payment export: use filters from PaymentManagementPage (`0` = all hubs), not header cookie. */
  const resolvePaymentExportHubmasterId = (
    exportAll: boolean,
    payload: Record<string, unknown>,
  ): number => {
    if (exportAll) return HUB_ID_EXPORT_ALL;
    if (
      payload.hubmasterid !== undefined &&
      payload.hubmasterid !== null &&
      payload.hubmasterid !== ""
    ) {
      const fromFilters = Number(payload.hubmasterid);
      if (Number.isFinite(fromFilters)) return fromFilters;
    }
    return resolveHubIdForExportApi(false);
  };

  /** Staff list API `hubid`: 0 = all hubs; positive = filter; null = no hub filter. */
  const resolveStaffExportHubId = (
    exportAll: boolean,
    payloadHubid?: unknown,
  ): number | null => {
    if (exportAll) return HUB_ID_EXPORT_ALL;

    const fromPayload = Number(payloadHubid);
    if (Number.isFinite(fromPayload) && fromPayload > 0) {
      return fromPayload;
    }

    if (isMultiHub && currentHubId) {
      const headerHub = Number(currentHubId);
      if (Number.isFinite(headerHub) && headerHub > 0) return headerHub;
    }

    const cookieHub = getHubId();
    if (cookieHub != null && Number.isFinite(cookieHub) && cookieHub > 0) {
      return cookieHub;
    }

    const authHub = (user as { hubid?: number } | null)?.hubid;
    if (authHub != null && Number.isFinite(Number(authHub)) && Number(authHub) > 0) {
      return Number(authHub);
    }

    return null;
  };

  /** Payment export: respect `paymentExportFilters.hubmasterid` (`0` = all hubs). */
  const filterPaymentExportRowsBySavedHub = (rows: any[]): any[] => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    try {
      const saved = localStorage.getItem("paymentExportFilters");
      if (!saved) return filterExportDataByHub(rows);
      const parsed = JSON.parse(saved) as { hubmasterid?: unknown };
      if (!("hubmasterid" in parsed)) return filterExportDataByHub(rows);
      const hubId = Number(parsed.hubmasterid);
      if (!Number.isFinite(hubId) || hubId <= 0) return rows;
      return rows.filter((item) => getItemHubId(item) === hubId);
    } catch {
      return filterExportDataByHub(rows);
    }
  };

  /** Client-side fallback when API returns rows outside the requested hub. */
  const filterExportDataByHub = (data: any[]): any[] => {
    const scopedHubId = resolveHubMasterIdForScopedExport();
    if (scopedHubId === HUB_ID_EXPORT_ALL) return data;

    const selectedHub = hubOptions.find(
      (h) => Number(h.hubmasterid) === scopedHubId,
    );
    const selectedHubName = selectedHub?.hubname?.trim().toLowerCase() ?? "";

    return data.filter((item: any) => {
      const itemHubId = getItemHubId(item);
      if (itemHubId != null) return itemHubId === scopedHubId;
      if (!selectedHubName) return false;
      const itemHubName = String(item?.hubname ?? item?.hubName ?? "")
        .trim()
        .toLowerCase();
      return itemHubName === selectedHubName;
    });
  };

  const transformSupportData = (data: any[]) => {
    const filteredData = filterExportDataByHub(data);

    return filteredData.map((ticket: any, index: number) => {
      const getSupportStatusDisplayName = (statusid: number): string => {
        const statusOption = allSupportStatusOptions.find(
          (option) => parseInt(option.codevalue) === statusid,
        );
        return statusOption ? statusOption.codedisplayname : "N/A";
      };

      const statusDisplayName = getSupportStatusDisplayName(
        ticket.statusid || 0,
      );

      return {
        "Ticket ID": ticket?.ticketid || "N/A",
        "User Name": ticket?.username || "N/A",
        "Mobile No.": ticket?.phoneno || "N/A",
        "Created At": DATE_FORMATS.formatDateTime(ticket?.enteredon || ""),
        "Hub Name": ticket?.hubname || "N/A",
        Category: ticket?.ticketcategoryid?.toString() || "Other",
        Priority: ticket?.ticketpriorityid?.toString() || "Medium",
        Assignee: ticket?.assignee?.toString() || "N/A",
        Status: statusDisplayName,
      };
    });
  };

  /** Booking export: respect `bookingExportFilters.filterModel.hubmasterid` (`0` = all hubs). */
  const filterBookingExportRowsBySavedHub = (rows: any[]): any[] => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    try {
      const saved = localStorage.getItem("bookingExportFilters");
      if (!saved) return filterExportDataByHub(rows);
      const parsed = JSON.parse(saved);
      const hubmasterid = parsed?.filterModel?.hubmasterid ?? parsed?.hubmasterid;
      if (hubmasterid === undefined || hubmasterid === null) return filterExportDataByHub(rows);
      const hubId = Number(hubmasterid);
      if (!Number.isFinite(hubId) || hubId <= 0) return rows;
      return rows.filter((item) => getItemHubId(item) === hubId);
    } catch {
      return filterExportDataByHub(rows);
    }
  };

  const transformBookingData = (data: any[]) => {
    const filteredData = filterBookingExportRowsBySavedHub(data);

    const rows: any[] = [];

    const getTripStatusDisplayName = (
      tripstatusid: number | null | undefined,
    ): string => {
      const statusId =
        tripstatusid === null || tripstatusid === undefined ? 1 : tripstatusid;
      const statusOption = allTripStatusOptions.find(
        (option) => parseInt(option.codevalue) === statusId,
      );
      return statusOption ? statusOption.codedisplayname : "Pending";
    };

    const formatDateForExcel = (
      dateString: string | null | undefined,
    ): string => {
      if (!dateString) return "N/A";
      try {
        const date = convertTimestampToIST(dateString);
        if (!date) return "N/A";
        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch {
        return "N/A";
      }
    };

    const getValueOrNA = (value: any): string => {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }
      return String(value);
    };

    filteredData.forEach((booking: any) => {
      const bookingId = booking?.bookingmasterid?.toString() || "N/A";
      const userName = getValueOrNA(booking?.name);
      const mobileNo = getValueOrNA(booking?.phonenumber);
      const planName = getValueOrNA(booking?.planname);
      const vehicleName = getValueOrNA(booking?.vehiclename);
      const vehicleNumber = getValueOrNA(booking?.vehiclenum);
      const registrationNumber = getValueOrNA(booking?.registrationnumber);
      const hubName = getValueOrNA(booking?.hubname);
      const rideStatus = getTripStatusDisplayName(booking?.tripstatus);

      const bookingPlan = booking?.bookingPlanModel || {};
      const planStartDate = formatDateForExcel(bookingPlan?.planstartdate);
      const planEndDate = formatDateForExcel(bookingPlan?.planenddate);
      const bookingAmount = bookingPlan?.bookingamount
        ? `₹${bookingPlan.bookingamount}`
        : "N/A";

      const hasRenewed = booking?.renewedPlan != null;
      const mainRow = {
        "Booking ID": bookingId,
        "User Name": userName,
        "Mobile No.": mobileNo,
        Plan: planName,
        Vehicle: vehicleName,
        "Vehicle Number": vehicleNumber,
        "Registration Number": registrationNumber,
        "Hub Name": hubName,
        "Plan Start Date": planStartDate,
        "Plan End Date": planEndDate,
        "Ride Status": rideStatus,
        "Booking Amount": bookingAmount,
        "Renewed Plan": hasRenewed ? "Yes" : "No",
        "Renewed Plan Start Date": formatDateForExcel(booking?.renewedPlanStartDate),
        "Renewed Plan End Date": formatDateForExcel(booking?.renewedPlanEndDate),
        "Renewed Plan Amount": booking?.renewedPlanAmount
          ? `₹${booking.renewedPlanAmount}`
          : "N/A",
        "Expiry Extended": booking?.expiryExtended ? "Yes" : "No",
        "Previous End Date": formatDateForExcel(booking?.previousEndDate),
        "Extended Date": formatDateForExcel(booking?.extendedDate),
        "Extension Reason": getValueOrNA(booking?.extensionReason),
      };

      rows.push(mainRow);
    });

    return rows;
  };

  // Ride data transformer function
  const transformRideData = (data: any[]) => {
    const rideList = Array.isArray(data) ? data : ((data as any)?.data ?? []);
    const filteredData = filterExportDataByHub(rideList);

    return filteredData.map((ride: any) => ({
      "Booking Id": ride?.bookingmasterid ?? "N/A",
      "User Name": ride?.name ?? "N/A",
      "Mobile No.": ride?.phonenumber ?? "N/A",
      "Vehicle Name": ride?.vehiclename ?? "N/A",
      Traveled: ride?.travelkm != null ? `${ride.travelkm}KM` : "N/A",
      "Due Date": DATE_FORMATS.formatDate(ride?.duedate || ""),
      Status: ride?.plandue === 1 ? "Overdue" : "On Time",
    }));
  };

  // Hub data transformer function
  const transformHubData = (data: any[]) => {
    let filteredData = filterExportDataByHub(data);

    try {
      const savedFilters = localStorage.getItem("hubExportFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);

        if (filters.hub) {
          filteredData = filteredData.filter(
            (hub: any) =>
              hub?.hubname === filters.hub || hub?.hubName === filters.hub,
          );
        }

        if (filters.city) {
          filteredData = filteredData.filter((hub: any) => {
            const hubCityId = parseInt(hub?.city?.toString() || "0");
            const hubStateId = parseInt(hub?.state?.toString() || "0");
            const matchedCity = citiesMaster.find(
              (c) =>
                Number(c.city_id) === hubCityId &&
                Number(c.state_id) === hubStateId,
            );
            return matchedCity?.city_name === filters.city;
          });
        }

        if (filters.status) {
          filteredData = filteredData.filter((hub: any) => {
            const statusValue = String(hub?.status || "");
            const statusOption = allPlanStatusOptions.find(
              (option) => option.codevalue === statusValue,
            );
            const statusDisplayName = statusOption
              ? statusOption.codedisplayname
              : statusValue;
            return statusDisplayName === filters.status;
          });
        }
      }
    } catch {}

    return filteredData.map((hub: any) => {
      const { hubImage, hubimage, ...rest } = hub;
      return rest;
    });
  };

  const transformStaffData = (data: any[]) => {
    const filteredData = filterExportDataByHub(data);

    return filteredData.map((staff: any) => {
      const getStatusText = (status: any) => {
        switch (String(status)) {
          case "0":
            return "Active";
          case "1":
            return "Inactive";
          case "2":
            return "Pending";
          case "3":
            return "Banned";
          default:
            return status || "N/A";
        }
      };

      return {
        "Staff ID": staff?.userid ?? "N/A",
        "Staff Name": staff?.name ?? "N/A",
        Role: staff?.rolename ?? "N/A",
        "Hub Name": staff?.hubname ?? "N/A",
        "Mobile No.": staff?.phonenumber ?? "N/A",
        Email: staff?.emailid ?? "N/A",
        Status: getStatusText(staff?.staffstatus),
        "Joining Date": DATE_FORMATS.formatDate(
          staff?.joiningdate || staff?.joineddate || "",
        ),
      };
    });
  };

  const transformPlanData = (data: any[]) => {
    const filteredData = filterExportDataByHub(data);

    return filteredData.map((plan: any) => {
      const getStatusDisplayName = (planstatusid: number): string => {
        const statusOption = allPlanStatusOptions.find(
          (option) => parseInt(option.codevalue) === planstatusid,
        );
        return statusOption ? statusOption.codedisplayname : "Unknown";
      };

      const statusString = getStatusDisplayName(plan.planstatusid || 0);
      const planCityId =
        parseInt(plan.city?.toString() || plan.cityId?.toString() || "0") || 0;
      const planStateId =
        parseInt(plan.state?.toString() || plan.stateId?.toString() || "0") ||
        0;
      const matchedCity = citiesMaster.find(
        (c) =>
          Number(c.city_id) === planCityId &&
          Number(c.state_id) === planStateId,
      );
      const cityDisplay = matchedCity?.city_name || "N/A";

      return {
        "Hub Name": plan.hubname || "N/A",
        City: cityDisplay,
        Status: statusString,
      };
    });
  };

  const transformVehicleData = (data: any[]) => {
    const filteredData = filterExportDataByHub(data);

    const getVehicleModelDisplayName = (
      vehiclemodelid: number | string | null | undefined,
    ): string => {
      if (vehiclemodelid === null || vehiclemodelid === undefined) return "N/A";
      const code = vehicleModelCodes.find(
        (c) => c.codevalue === String(vehiclemodelid),
      );
      return code?.codedisplayname || "N/A";
    };

    const getVehicleStatusText = (status: any): string => {
      const statusOption = allVehicleStatusOptions.find(
        (option) => option.codevalue === String(status),
      );
      return statusOption
        ? statusOption.codedisplayname
        : String(status) || "N/A";
    };

    const getVehicleTypeText = (vehicleType: any): string => {
      if (
        !vehicleType ||
        !vehicleTypeOptions ||
        vehicleTypeOptions.length === 0
      )
        return "N/A";
      const typeOption = vehicleTypeOptions.find(
        (option) => option.codevalue === String(vehicleType),
      );
      return typeOption ? typeOption.codedisplayname : "N/A";
    };

    const getSpeedTypeText = (speedType: any): string => {
      if (!speedType || !evSpeedTypeOptions || evSpeedTypeOptions.length === 0)
        return "N/A";
      const speedOption = evSpeedTypeOptions.find(
        (option) => option.codevalue === String(speedType),
      );
      return speedOption ? speedOption.codedisplayname : "N/A";
    };

    const getHubName = (hubId: any): string => {
      if (!hubId || !hubOptions || hubOptions.length === 0) return "N/A";
      const hub = hubOptions.find(
        (h) =>
          h.hubmasterid === hubId || String(h.hubmasterid) === String(hubId),
      );
      return hub ? hub.hubname : "N/A";
    };

    return filteredData.map((vehicle: any) => {
      const vehiclemodelid =
        vehicle.vehiclemodelid || vehicle.vehiclemodel || null;
      const modelName = getVehicleModelDisplayName(vehiclemodelid);

      return {
        "Vehicle Name": vehicle.vehiclename || "N/A",
        Model: modelName,
        "Range (km/charge)": vehicle.rangeperchargekm || "N/A",
        "Top Speed (kmph)": vehicle.topspeed || "N/A",
        Weight: vehicle.weight || "N/A",
        "Battery No 1": vehicle.batteryno1 || "N/A",
        "Battery No 2": vehicle.batteryno2 || "N/A",
        "Charge No.": vehicle.chargeno || "N/A",
        "Registration No.": vehicle.registrationnumber || "N/A",
        "Vehicle Number": vehicle.vehiclenum || "N/A",
        "Telematics Device ID": vehicle.telematicsdeviceid || "N/A",
        "Assign Hub": getHubName(vehicle.hubmasterid),
        Status: getVehicleStatusText(vehicle.status),
        "Vehicle Type": getVehicleTypeText(vehicle.vehicletype),
        "Speed Type": getSpeedTypeText(vehicle.speedtype),
        "Motor ID": vehicle.motorid || "N/A",
        "Other Specifications": vehicle.otherspecifications || "N/A",
      };
    });
  };

  const transformSupportDataAll = (data: any[]) => {
    const rows = Array.isArray(data) ? data : [];
    const getSupportStatusDisplayName = (statusid: number): string => {
      const statusOption = allSupportStatusOptions.find(
        (option) => parseInt(option.codevalue) === statusid,
      );
      return statusOption ? statusOption.codedisplayname : "N/A";
    };

    return rows.map((ticket: any) => {
      const statusDisplayName = getSupportStatusDisplayName(
        ticket.statusid || 0,
      );

      return {
        "Ticket ID": ticket?.ticketid || "N/A",
        "User Name": ticket?.username || "N/A",
        "Mobile No.": ticket?.phoneno || "N/A",
        "Created At": DATE_FORMATS.formatDateTime(ticket?.enteredon || ""),
        "Hub Name": ticket?.hubname || "N/A",
        Category: ticket?.ticketcategoryid?.toString() || "Other",
        Priority: ticket?.ticketpriorityid?.toString() || "Medium",
        Assignee: ticket?.assignee?.toString() || "N/A",
        Status: statusDisplayName,
      };
    });
  };

  const transformBookingDataAll = (data: any[]) => {
    const filteredData = data;

    const rows: any[] = [];

    const getTripStatusDisplayName = (
      tripstatusid: number | null | undefined,
    ): string => {
      const statusId =
        tripstatusid === null || tripstatusid === undefined ? 1 : tripstatusid;
      const statusOption = allTripStatusOptions.find(
        (option) => parseInt(option.codevalue) === statusId,
      );
      return statusOption ? statusOption.codedisplayname : "Pending";
    };

    const formatDateForExcel = (
      dateString: string | null | undefined,
    ): string => {
      if (!dateString) return "N/A";
      try {
        const date = convertTimestampToIST(dateString);
        if (!date) return "N/A";
        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch {
        return "N/A";
      }
    };

    const getValueOrNA = (value: any): string => {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }
      return String(value);
    };

    filteredData.forEach((booking: any) => {
      const bookingId = booking?.bookingmasterid?.toString() || "N/A";
      const userName = getValueOrNA(booking?.name);
      const mobileNo = getValueOrNA(booking?.phonenumber);
      const planName = getValueOrNA(booking?.planname);
      const vehicleName = getValueOrNA(booking?.vehiclename);
      const vehicleNumber = getValueOrNA(booking?.vehiclenum);
      const registrationNumber = getValueOrNA(booking?.registrationnumber);
      const hubName = getValueOrNA(booking?.hubname);
      const rideStatus = getTripStatusDisplayName(booking?.tripstatus);

      const bookingPlan = booking?.bookingPlanModel || {};
      const planStartDate = formatDateForExcel(bookingPlan?.planstartdate);
      const planEndDate = formatDateForExcel(bookingPlan?.planenddate);
      const bookingAmount = bookingPlan?.bookingamount
        ? `₹${bookingPlan.bookingamount}`
        : "N/A";

      const hasRenewed = booking?.renewedPlan != null;
      const mainRow = {
        "Booking ID": bookingId,
        "User Name": userName,
        "Mobile No.": mobileNo,
        Plan: planName,
        Vehicle: vehicleName,
        "Vehicle Number": vehicleNumber,
        "Registration Number": registrationNumber,
        "Hub Name": hubName,
        "Plan Start Date": planStartDate,
        "Plan End Date": planEndDate,
        "Ride Status": rideStatus,
        "Booking Amount": bookingAmount,
        "Renewed Plan": hasRenewed ? "Yes" : "No",
        "Renewed Plan Start Date": formatDateForExcel(booking?.renewedPlanStartDate),
        "Renewed Plan End Date": formatDateForExcel(booking?.renewedPlanEndDate),
        "Renewed Plan Amount": booking?.renewedPlanAmount
          ? `₹${booking.renewedPlanAmount}`
          : "N/A",
        "Expiry Extended": booking?.expiryExtended ? "Yes" : "No",
        "Previous End Date": formatDateForExcel(booking?.previousEndDate),
        "Extended Date": formatDateForExcel(booking?.extendedDate),
        "Extension Reason": getValueOrNA(booking?.extensionReason),
      };

      rows.push(mainRow);
    });

    return rows;
  };

  const transformRideDataAll = (data: any[]) => {
    const rideList = Array.isArray(data) ? data : ((data as any)?.data ?? []);
    const filteredData = rideList;

    return filteredData.map((ride: any) => ({
      "Booking Id": ride?.bookingmasterid ?? "N/A",
      "User Name": ride?.name ?? "N/A",
      "Mobile No.": ride?.phonenumber ?? "N/A",
      "Vehicle Name": ride?.vehiclename ?? "N/A",
      Traveled: ride?.travelkm != null ? `${ride.travelkm}KM` : "N/A",
      "Due Date": DATE_FORMATS.formatDate(ride?.duedate || ""),
      Status: ride?.plandue === 1 ? "Overdue" : "On Time",
    }));
  };

  const transformHubDataAll = (data: any[]) => {
    const filteredData = data;

    return filteredData.map((hub: any) => {
      const { hubImage, hubimage, ...rest } = hub;
      return rest;
    });
  };

  const transformStaffDataAll = (data: any[]) => {
    const filteredData = data;

    return filteredData.map((staff: any) => {
      const getStatusText = (status: any) => {
        switch (String(status)) {
          case "0":
            return "Active";
          case "1":
            return "Inactive";
          case "2":
            return "Pending";
          case "3":
            return "Banned";
          default:
            return status || "N/A";
        }
      };

      return {
        "Staff ID": staff?.userid ?? "N/A",
        "Staff Name": staff?.name ?? "N/A",
        Role: staff?.rolename ?? "N/A",
        "Hub Name": staff?.hubname ?? "N/A",
        "Mobile No.": staff?.phonenumber ?? "N/A",
        Email: staff?.emailid ?? "N/A",
        Status: getStatusText(staff?.staffstatus),
        "Joining Date": DATE_FORMATS.formatDate(
          staff?.joiningdate || staff?.joineddate || "",
        ),
      };
    });
  };

  const transformPlanDataAll = (data: any[]) => {
    const filteredData = data;

    return filteredData.map((plan: any) => {
      const getStatusDisplayName = (planstatusid: number): string => {
        const statusOption = allPlanStatusOptions.find(
          (option) => parseInt(option.codevalue) === planstatusid,
        );
        return statusOption ? statusOption.codedisplayname : "Unknown";
      };

      const statusString = getStatusDisplayName(plan.planstatusid || 0);
      const planCityId =
        parseInt(plan.city?.toString() || plan.cityId?.toString() || "0") || 0;
      const planStateId =
        parseInt(plan.state?.toString() || plan.stateId?.toString() || "0") ||
        0;
      const matchedCity = citiesMaster.find(
        (c) =>
          Number(c.city_id) === planCityId &&
          Number(c.state_id) === planStateId,
      );
      const cityDisplay = matchedCity?.city_name || "N/A";

      return {
        "Hub Name": plan.hubname || "N/A",
        City: cityDisplay,
        Status: statusString,
      };
    });
  };

  const transformVehicleDataAll = (data: any[]) => {
    const filteredData = data;

    const getVehicleModelDisplayName = (
      vehiclemodelid: number | string | null | undefined,
    ): string => {
      if (vehiclemodelid === null || vehiclemodelid === undefined) return "N/A";
      const code = vehicleModelCodes.find(
        (c) => c.codevalue === String(vehiclemodelid),
      );
      return code?.codedisplayname || "N/A";
    };

    const getVehicleStatusText = (status: any): string => {
      const statusOption = allVehicleStatusOptions.find(
        (option) => option.codevalue === String(status),
      );
      return statusOption
        ? statusOption.codedisplayname
        : String(status) || "N/A";
    };

    const getVehicleTypeText = (vehicleType: any): string => {
      if (
        !vehicleType ||
        !vehicleTypeOptions ||
        vehicleTypeOptions.length === 0
      )
        return "N/A";
      const typeOption = vehicleTypeOptions.find(
        (option) => option.codevalue === String(vehicleType),
      );
      return typeOption ? typeOption.codedisplayname : "N/A";
    };

    const getSpeedTypeText = (speedType: any): string => {
      if (!speedType || !evSpeedTypeOptions || evSpeedTypeOptions.length === 0)
        return "N/A";
      const speedOption = evSpeedTypeOptions.find(
        (option) => option.codevalue === String(speedType),
      );
      return speedOption ? speedOption.codedisplayname : "N/A";
    };

    const getHubName = (hubId: any): string => {
      if (!hubId || !hubOptions || hubOptions.length === 0) return "N/A";
      const hub = hubOptions.find(
        (h) =>
          h.hubmasterid === hubId || String(h.hubmasterid) === String(hubId),
      );
      return hub ? hub.hubname : "N/A";
    };

    return filteredData.map((vehicle: any) => {
      const vehiclemodelid =
        vehicle.vehiclemodelid || vehicle.vehiclemodel || null;
      const modelName = getVehicleModelDisplayName(vehiclemodelid);

      return {
        "Vehicle Name": vehicle.vehiclename || "N/A",
        Model: modelName,
        "Range (km/charge)": vehicle.rangeperchargekm || "N/A",
        "Top Speed (kmph)": vehicle.topspeed || "N/A",
        Weight: vehicle.weight || "N/A",
        "Battery No 1": vehicle.batteryno1 || "N/A",
        "Battery No 2": vehicle.batteryno2 || "N/A",
        "Charge No.": vehicle.chargeno || "N/A",
        "Registration No.": vehicle.registrationnumber || "N/A",
        "Vehicle Number": vehicle.vehiclenum || "N/A",
        "Telematics Device ID": vehicle.telematicsdeviceid || "N/A",
        "Assign Hub": getHubName(vehicle.hubmasterid),
        Status: getVehicleStatusText(vehicle.status),
        "Vehicle Type": getVehicleTypeText(vehicle.vehicletype),
        "Speed Type": getSpeedTypeText(vehicle.speedtype),
        "Motor ID": vehicle.motorid || "N/A",
        "Other Specifications": vehicle.otherspecifications || "N/A",
      };
    });
  };

  const formatPaymentDiscountedAmountForExport = (p: any): string => {
    const raw = p?.discountedamount ?? p?.discountedAmount;
    if (raw == null || raw === "") return "";
    const n = typeof raw === "number" ? raw : Number.parseFloat(String(raw));
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const transformPaymentDataAll = (data: any[]) => {
    const rows = Array.isArray(data)
      ? data
      : ((data as any)?.data ?? (data as any)?.result ?? []);
    const filteredData = filterPaymentExportRowsBySavedHub(rows);

    const formatDateForExcel = (
      dateString: string | null | undefined,
    ): string => {
      if (!dateString) return "N/A";
      try {
        const date = convertTimestampToIST(dateString);
        if (!date) return "N/A";
        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch {
        return "N/A";
      }
    };

    return filteredData.map((p: any) => ({
      "Payment ID": p.paymentid ?? "N/A",
      "Booking ID": p.bookingid ?? "N/A",
      "User Name": p.username ?? "N/A",
      "Mobile Number": p.mobileno ?? "N/A",
      "Hub Name": p.hubname ?? "N/A",
      Amount: p.amount ?? "N/A",
      "Discounted Amount": formatPaymentDiscountedAmountForExport(p),
      "Payment Method": ((): string => {
        const method = p.paymentmethod;
        if (method === 1) return "Card";
        if (method === 2) return "UPI";
        if (method === 3) return "Wallet";
        if (method === 4) return "Net Banking";
        return String(method ?? "N/A");
      })(),
      "Transaction ID": p.transactionid ?? "N/A",
      "Summary of Payments": p.summeryofpayments ?? "N/A",
      "Payment Date": formatDateForExcel(p.enteredon),
    }));
  };

  const getPageTitle = () => {
    // Handle dynamic routes first
    if (location.pathname.startsWith("/ride-management/ride-details/")) {
      return "Ride Management";
    }
    if (location.pathname.startsWith("/staff-management/edit-staff/")) {
      return "Staff Management";
    }
    if (location.pathname.startsWith("/staff-management/create-staff")) {
      return "Staff Management";
    }
    if (location.pathname.startsWith("/hub-management/edit-hub/")) {
      return "Hub Management";
    }
    if (location.pathname.startsWith("/hub-management/create-hub")) {
      return "Hub Management";
    }
    if (location.pathname.startsWith("/vehicle-management/edit-vehicle/")) {
      return "Vehicle Management";
    }
    if (location.pathname.startsWith("/vehicle-management/create-vehicle")) {
      return "Vehicle Management";
    }
    if (location.pathname.startsWith("/plan-management/edit-plan/")) {
      return "Plan Management";
    }
    if (location.pathname.startsWith("/plan-management/create-plan")) {
      return "Plan Management";
    }
    if (location.pathname.startsWith("/support/details")) {
      return "Support System";
    }
    if (location.pathname.startsWith("/support/create-support")) {
      return "Support System";
    }
    if (location.pathname.startsWith("/booking-management/details")) {
      return "Booking Management";
    }
    if (location.pathname.startsWith("/booking-management/create-booking")) {
      return "Booking Management";
    }
    if (location.pathname.startsWith("/payment-management/details")) {
      return "Payment Management";
    }
    if (location.pathname.startsWith("/user-management/user-details")) {
      return "User Management";
    }
    if (location.pathname.startsWith("/refund-management/edit-refund")) {
      return "Refund Management";
    }

    // Handle exact matches
    const titles: Record<string, string> = {
      "/dashboard": "Dashboard",
      "/user-management": "User Management",
      "/user/profile": "User Profile",
      "/staff-management": "Staff Management",
      "/hub-management": "Hub Management",
      "/vehicle-management": "Vehicle Management",
      "/plan-management": "Plan Management",
      "/payment-management": "Payment Management",
      "/booking-management": "Booking Management",
      "/ride-management": "Ride Management",
      "/support": "Support System",
      "/marketing/promo-codes": "Marketing",
      "/marketing/promo-codes/create": "Marketing",
      "/marketing/referral-settings": "Marketing",
      "/settings": "Setting",
      "/refund-management": "Refund Management",
    };
    return titles[location.pathname] || "Dashboard";
  };

  const renderBreadcrumb = (
    basePath: string,
    label: string,
    highlight: string,
    navigateTo: string,
  ) => {
    if (location.pathname === basePath) {
      return (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="text"
            className="p-0 me-3"
            onClick={() => {
              /** Match EditBookingPage: restore list filters when we navigated here with them; otherwise clear export presets + refetch. */
              if (
                basePath === "/booking-management/create-booking" &&
                navigateTo === "/booking-management"
              ) {
                clearCreateBookingDraft();
                const st = location.state as
                  | { filters?: unknown; page?: number }
                  | null
                  | undefined;
                if (st?.filters) {
                  navigate(navigateTo, {
                    state: { filters: st.filters, page: st.page },
                  });
                } else {
                  try {
                    localStorage.removeItem("bookingExportFilters");
                  } catch {
                    // ignore
                  }
                  navigate(navigateTo, { state: { shouldRefetch: true } });
                }
                return;
              }
              if (
                navigateTo === "/booking-management" ||
                navigateTo === "/support" ||
                navigateTo === "/user-management"
              ) {
                navigate(navigateTo, { state: location.state });
              } else {
                navigate(navigateTo);
              }
            }}
            sx={{ minWidth: "auto" }}
          >
            <img src={BackIcon} alt="Back" width={30} height={30} />
          </Button>
          <Typography
            variant="h6"
            noWrap
            className="fw-bold"
            sx={{ fontSize: "1.5rem" }}
          >
            {label} / <span style={{ color: "#ff6b35" }}>{highlight}</span>
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  const renderPageTitle = () => {
    const createPaths: Record<string, { base: string; create: string }> = {
      "/staff-management/create-staff": {
        base: "Staff Management",
        create: "Create Staff",
      },
      "/hub-management/create-hub": {
        base: "Hub Management",
        create: "Create Hub",
      },
      "/vehicle-management/create-vehicle": {
        base: "Vehicle Management",
        create: "Create Vehicle",
      },
      "/plan-management/create-plan": {
        base: "Plan Management",
        create: "Create Plan",
      },
      "/booking-management/create-booking": {
        base: "Booking Management",
        create: "Create Booking",
      },
    };

    // Handle edit paths with dynamic IDs
    const editPaths: Record<string, { base: string; edit: string }> = {
      "/staff-management/edit-staff": {
        base: "Staff Management",
        edit: "Edit Staff",
      },
      "/staff-management/staff-details": {
        base: "Staff Management",
        edit: "Staff Details",
      },
      "/hub-management/edit-hub": {
        base: "Hub Management",
        edit: "Edit Hub",
      },
      "/hub-management/hub-details": {
        base: "Hub Management",
        edit: "Hub Details",
      },
      "/vehicle-management/edit-vehicle": {
        base: "Vehicle Management",
        edit: "Edit Vehicle",
      },
      "/vehicle-management/vehicle-details": {
        base: "Vehicle Management",
        edit: "Vehicle Details",
      },
      "/plan-management/edit-plan": {
        base: "Plan Management",
        edit: "Edit Plan",
      },
      "/plan-management/plan-details": {
        base: "Plan Management",
        edit: "Plan Details",
      },
      "/payment-management/details": {
        base: "Payment Management",
        edit: "Payment Details",
      },
      "/refund-management/edit-refund": {
        base: "Refund Management",
        edit: "Refund Details",
      },
    };

    // Check for edit paths with dynamic IDs
    const editPathMatch = Object.keys(editPaths).find((path) =>
      location.pathname.startsWith(path),
    );

    if (editPathMatch) {
      const editPath = editPaths[editPathMatch];
      return (
        <>
          {editPath.base} /{" "}
          <span style={{ color: "rgba(240, 78, 35, 1)" }}>{editPath.edit}</span>
        </>
      );
    }

    const createPath = createPaths[location.pathname];
    if (createPath) {
      return (
        <>
          {createPath.base} /{" "}
          <span style={{ color: "rgba(240, 78, 35, 1)" }}>
            {createPath.create}
          </span>
        </>
      );
    }

    if (location.pathname === "/marketing/promo-codes/create") {
      return (
        <>
          Marketing /{" "}
          <span style={{ color: "rgba(240, 78, 35, 1)" }}>
            {promoCodesCreateIsEdit
              ? "Edit Promo Code"
              : "Create New Promocode"}
          </span>
        </>
      );
    }
    if (location.pathname === "/marketing/promo-codes") {
      return (
        <>
          Marketing /{" "}
          <span style={{ color: "rgba(240, 78, 35, 1)" }}>Promo Code</span>
        </>
      );
    }
    if (location.pathname === "/marketing/referral-settings") {
      return (
        <>
          Marketing /{" "}
          <span style={{ color: "rgba(240, 78, 35, 1)" }}>
            Referral Settings
          </span>
        </>
      );
    }

    return getPageTitle();
  };

  const hiddenPages = [
    "/dashboard",
    "/user-management",
    "/user-management/user-details",
    "/user/profile",
    "/payment-management",
    "/booking-management",
    "/booking-management/details",
    "/ride-management",
    "/support",
    "/support/details",
    "/staff-management/create-staff",
    "/staff-management/edit-staff",
    "/staff-management/staff-details",
    "/hub-management/create-hub",
    "/hub-management/edit-hub",
    "/hub-management/hub-details",
    "/vehicle-management/create-vehicle",
    "/vehicle-management/edit-vehicle",
    "/vehicle-management/vehicle-details",
    "/plan-management/create-plan",
    "/plan-management/edit-plan",
    "/plan-management/plan-details",
    "/payment-management/details",
    "/booking-management/create-booking",
    "/booking-management/edit-booking",
    "/support/create-support",
    "/refund-management",
  ];

  const shouldShowCreateButton = () => {
    if (location.pathname.startsWith("/refund-management")) {
      return false;
    }
    if (location.pathname === "/settings") {
      return false;
    }
    if (
      location.pathname === "/marketing/promo-codes/create" ||
      location.pathname === "/marketing/referral-settings"
    ) {
      return false;
    }
    if (location.pathname === "/marketing/promo-codes") {
      return true;
    }
    const bookingPath = location.pathname.replace(/\/$/, "");
    if (bookingPath === "/booking-management") {
      // TODO: Temporarily disabled for publish — uncomment next line to restore header "Create Booking".
      return canCreate();
    }

    const isHidden = hiddenPages.some((hiddenPath) =>
      location.pathname.startsWith(hiddenPath),
    );

    const hasCreatePermission = canCreate();

    if (location.pathname === "/hub-management") {
      const isSuperAdmin = user?.roleid === 1;
      return !isHidden && hasCreatePermission && isSuperAdmin;
    }

    if (
      location.pathname === "/staff-management" ||
      location.pathname === "/vehicle-management"
    ) {
      const canCreateStaffOrVehicle =
        user?.roleid && [1, 2, 7, 10].includes(user.roleid);
      return !isHidden && hasCreatePermission && canCreateStaffOrVehicle;
    }

    return !isHidden && hasCreatePermission;
  };

  const getCreateButtonLabel = () => {
    const labels: Record<string, string> = {
      "/staff-management": "Create Staff",
      "/hub-management": "Create Hub",
      "/vehicle-management": "Create Vehicle",
      "/plan-management": "Penalty Setup",
      "/booking-management": "Create Booking",
      "/marketing/promo-codes": "Create New Promocode",
    };

    // Check for /marketing/promo-codes
    if (location.pathname === "/marketing/promo-codes/create") {
      return promoCodesCreateIsEdit
        ? "Edit Promo Code"
        : "Create New Promocode";
    }
    return labels[location.pathname] || "Create New Promocode";
  };

  const shouldShowExportButton = () => {
    if (location.pathname === "/settings") {
      return false;
    }

    const exportPages = [
      "/payment-management",
      "/booking-management",
      "/support",
      "/staff-management",
      "/plan-management",
      "/hub-management",
      "/vehicle-management",
      "/ride-management",
    ];

    if (!exportPages.includes(location.pathname)) {
      return false;
    }

    const restrictedPages = [
      "/hub-management",
      "/staff-management",
      "/vehicle-management",
      "/payment-management",
      "/booking-management",
      "/ride-management",
      "/support",
    ];

    if (restrictedPages.includes(location.pathname)) {
      const hasRestrictedExportPermission =
        user?.roleid && [1, 2, 5, 7, 10].includes(user.roleid);
      return hasRestrictedExportPermission;
    }

    return canExport();
  };

  const shouldShowExportAllButton = () => {
    if (!shouldShowExportButton()) {
      return false;
    }
    return isMultiHub && !!currentHubId;
  };

  const getExportApiEndpoint = () => {
    switch (location.pathname) {
      case "/staff-management":
        return API_ENDPOINTS.STAFF.LIST;
      case "/booking-management":
        return API_ENDPOINTS.BOOKINGS.EXPORT_BOOKING_DETAILS;
      case "/ride-management":
        return API_ENDPOINTS.RIDES.LIST;
      case "/plan-management":
        return API_ENDPOINTS.PLANS.LIST;
      case "/hub-management":
        return API_ENDPOINTS.HUBS.LIST;
      case "/vehicle-management":
        return API_ENDPOINTS.VEHICLES.LIST;
      case "/support":
        return API_ENDPOINTS.SUPPORT.LIST;
      case "/payment-management":
        return API_ENDPOINTS.PAYMENTS.SEARCH;
      default:
        return API_ENDPOINTS.STAFF.LIST;
    }
  };

  const applyExportHubScopeToPayload = (
    payload: Record<string, unknown>,
    exportAll: boolean,
  ): Record<string, unknown> => {
    const hubId = resolveHubIdForExportApi(exportAll);
    const next: Record<string, unknown> = { ...payload };

    if (
      next.filterModel != null &&
      typeof next.filterModel === "object" &&
      !Array.isArray(next.filterModel)
    ) {
      let targetHubId = hubId;
      if (location.pathname === "/booking-management") {
        const existingHubId = (next.filterModel as Record<string, unknown>).hubmasterid;
        if (exportAll) {
          targetHubId = HUB_ID_EXPORT_ALL;
        } else if (existingHubId !== undefined && existingHubId !== null && existingHubId !== "") {
          const fromFilters = Number(existingHubId);
          if (Number.isFinite(fromFilters)) {
            targetHubId = fromFilters;
          }
        }
      }

      next.filterModel = {
        ...(next.filterModel as Record<string, unknown>),
        hubmasterid: targetHubId,
      };
    }

    if (location.pathname === "/payment-management") {
      next.hubmasterid = resolvePaymentExportHubmasterId(exportAll, next);
    } else if (
      "hubmasterid" in next ||
      location.pathname === "/ride-management" ||
      location.pathname === "/vehicle-management"
    ) {
      next.hubmasterid = hubId;
    }
    if ("hubid" in next || location.pathname === "/staff-management") {
      next.hubid = resolveStaffExportHubId(exportAll, next.hubid);
    }

    return next;
  };

  const getExportApiPayload = (exportAll = false) => {
    let payload: Record<string, unknown> = {};

    switch (location.pathname) {
      case "/payment-management": {
        try {
          const savedFilters = localStorage.getItem("paymentExportFilters");
          payload = savedFilters ? JSON.parse(savedFilters) : {};
        } catch {
          payload = {};
        }
        break;
      }
      case "/staff-management": {
        const staffDefaults = {
          name: null as string | null,
          roleid: null as number | null,
          userstatus: null as number | null,
          kycstatus: null as number | null,
          registrationdate: null as string | null,
          reporttype: "staff",
          pageNumber: 1,
          pageSize: 10000,
        };
        try {
          const saved = localStorage.getItem("staffExportFilters");
          if (saved) {
            const parsed = JSON.parse(saved) as Record<string, unknown>;
            payload = {
              ...staffDefaults,
              ...parsed,
              hubid: resolveStaffExportHubId(exportAll, parsed.hubid),
              pageNumber: 1,
              pageSize: 10000,
            };
            break;
          }
        } catch {
          /* ignore */
        }
        payload = {
          ...staffDefaults,
          hubid: resolveStaffExportHubId(exportAll),
        };
        break;
      }
      case "/booking-management": {
        // Prefer persisted filters saved by BookingManagementPage
        try {
          const saved = localStorage.getItem("bookingExportFilters");
          if (saved) {
            const parsed = JSON.parse(saved || "{}");
            // If the saved data already has filterModel, use it directly
            if (parsed.filterModel) {
              payload = parsed;
              break;
            }
            // Otherwise, wrap the old format in filterModel
            payload = {
              filterModel: {
                fromDate: parsed.fromDate || null,
                toDate: parsed.toDate || null,
                rideStatus: parsed.rideStatus ?? null,
                bookingId: parsed.bookingId ?? 0,
                userName: parsed.userName ?? "",
                mobileNumber: parsed.mobileNumber ?? "",
                planId: parsed.planId ?? 0,
                vehicleName: parsed.vehicleName ?? "",
                vehicleNumber: parsed.vehicleNumber ?? "",
                registrationNumber: parsed.registrationNumber ?? "",
              },
            };
            break;
          }
        } catch {}
        // Fallback to sensible defaults
        const now = new Date();
        const from = new Date(
          now.getFullYear(),
          now.getMonth(),
          1,
        ).toISOString();
        const to = now.toISOString();
        payload = {
          filterModel: {
            fromDate: from,
            toDate: to,
            rideStatus: null,
            bookingId: 0,
            userName: "",
            mobileNumber: "",
            planId: 0,
            vehicleName: "",
            vehicleNumber: "",
            registrationNumber: "",
          },
        };
        break;
      }
      case "/ride-management": {
        try {
          const saved = localStorage.getItem("rideExportFilters");
          payload = saved
            ? JSON.parse(saved)
            : { bookingid: null, duedate: null };
        } catch {
          payload = { bookingid: null, duedate: null };
        }
        break;
      }
      case "/plan-management":
        payload = {};
        break;
      case "/hub-management":
        payload = {};
        break;
      case "/vehicle-management": {
        const vehicleDefaults = {
          pageNumber: 1,
          pageSize: 10000,
          vehiclenum: "",
          registrationnumber: "",
          telematicsid: "",
          vehiclemodel: 0,
          hubmasterid: resolveHubIdForExportApi(exportAll),
          status: 0,
        };
        try {
          const saved = localStorage.getItem("vehicleExportFilters");
          if (saved) {
            const parsed = JSON.parse(saved) as Record<string, unknown>;
            payload = {
              ...parsed,
              ...vehicleDefaults,
              pageNumber: 1,
              pageSize: 10000,
            };
            break;
          }
        } catch {
          /* ignore */
        }
        payload = vehicleDefaults;
        break;
      }
      case "/support": {
        const defaults = {
          userId: user?.userid ?? 0,
          pageNumber: 1,
          pageSize: 10000,
          ticketid: "",
          createdDate: null as string | null,
          assignee: 0,
          ticketpriorityid: 0,
          statusid: 0,
          ticketcategoryid: null as number | null,
        };
        try {
          const saved = localStorage.getItem("supportExportFilters");
          if (saved) {
            const parsed = JSON.parse(saved) as Record<string, unknown>;
            if (parsed && typeof parsed === "object") {
              payload = {
                ...defaults,
                ...parsed,
                pageNumber: 1,
                pageSize:
                  typeof parsed.pageSize === "number" ? parsed.pageSize : 10000,
              };
              break;
            }
          }
        } catch {
          /* ignore */
        }
        payload = defaults;
        break;
      }
      default:
        payload = {};
    }

    return applyExportHubScopeToPayload(payload, exportAll);
  };

  const getExportCustomApiCall = (exportAll = false) => {
    const hubmasterid = resolveHubIdForExportApi(exportAll);

    if (location.pathname === "/payment-management") {
      return async (payload: any) => {
        const paymentPayload =
          payload != null && typeof payload === "object"
            ? (payload as Record<string, unknown>)
            : {};
        const response = await apiService.searchPaymentDetails({
          ...payload,
          hubmasterid: resolvePaymentExportHubmasterId(
            exportAll,
            paymentPayload,
          ),
          pageNumber: payload?.pageNumber ?? 1,
          pageSize: payload?.pageSize ?? 10000,
        });
        const { items } = parseSearchPaymentDetailsResponse(response);
        return items;
      };
    }
    if (location.pathname === "/booking-management") {
      return (payload: any) => {
        const filterModel = payload?.filterModel ?? payload ?? {};
        const rideStatusRaw = filterModel?.rideStatus;
        const rideStatus =
          rideStatusRaw === null ||
          rideStatusRaw === undefined ||
          rideStatusRaw === ""
            ? null
            : Number(rideStatusRaw);
        return apiService.exportBookingDetails({
          fromDate: filterModel?.fromDate ?? null,
          toDate: filterModel?.toDate ?? null,
          bookingId: Number(filterModel?.bookingId) || 0,
          userName: filterModel?.userName ?? "",
          mobileNumber: filterModel?.mobileNumber ?? "",
          planId: Number(filterModel?.planId) || 0,
          vehicleName: filterModel?.vehicleName ?? "",
          vehicleNumber: filterModel?.vehicleNumber ?? "",
          registrationNumber: filterModel?.registrationNumber ?? "",
          rideStatus:
            rideStatus != null && Number.isFinite(rideStatus) ? rideStatus : null,
          hubmasterid: filterModel?.hubmasterid !== undefined && filterModel?.hubmasterid !== null ? Number(filterModel.hubmasterid) : hubmasterid,
        });
      };
    }
    if (location.pathname === "/support") {
      return (payload: any) => apiService.searchSupportTickets(payload);
    }
    if (location.pathname === "/ride-management") {
      return (payload: any) =>
        apiService.getRideManagementData({
          ...payload,
          hubmasterid,
          pageNumber: payload?.pageNumber ?? 1,
          pageSize: payload?.pageSize ?? 10000,
        });
    }
    if (location.pathname === "/staff-management") {
      return async (payload: any) => {
        const hubid = resolveStaffExportHubId(exportAll, payload?.hubid);
        const response = await apiService.getAllUsers({
          name:
            typeof payload?.name === "string"
              ? payload.name.trim() || null
              : null,
          roleid: payload?.roleid ?? null,
          hubid,
          userstatus: payload?.userstatus ?? null,
          kycstatus: payload?.kycstatus ?? null,
          registrationdate: payload?.registrationdate ?? null,
          reporttype: "staff",
          pageNumber: Number(payload?.pageNumber ?? 1) || 1,
          pageSize: Number(payload?.pageSize ?? 10000) || 10000,
        });
        const { items } = parseGetAllUsersResponse(response);
        return items;
      };
    }
    if (location.pathname === "/vehicle-management") {
      return async (payload: any) => {
        const response = await apiService.getAllVehicles({
          pageNumber: Number(payload?.pageNumber ?? 1) || 1,
          pageSize: Number(payload?.pageSize ?? 10000) || 10000,
          vehiclenum: String(payload?.vehiclenum ?? ""),
          registrationnumber: String(payload?.registrationnumber ?? ""),
          telematicsid: String(payload?.telematicsid ?? ""),
          vehiclemodel: Number(payload?.vehiclemodel ?? 0) || 0,
          hubmasterid,
          status: Number(payload?.status ?? 0) || 0,
        });
        const root =
          (response as { data?: unknown })?.data !== undefined
            ? (response as { data?: unknown }).data
            : response;
        if (Array.isArray(root)) return root;
        if (root != null && typeof root === "object") {
          const r = root as Record<string, unknown>;
          const items =
            (Array.isArray(r.vehicles) && r.vehicles) ||
            (Array.isArray(r.result) && r.result) ||
            (Array.isArray(r.data) && r.data) ||
            (Array.isArray(r.items) && r.items) ||
            [];
          return items;
        }
        return [];
      };
    }
    return undefined;
  };

  const getExportDataTransformer = () => {
    if (location.pathname === "/support") return transformSupportData;
    if (location.pathname === "/booking-management")
      return transformBookingData;
    if (location.pathname === "/ride-management") return transformRideData;
    if (location.pathname === "/payment-management")
      return transformPaymentData;
    if (location.pathname === "/hub-management") return transformHubData;
    if (location.pathname === "/staff-management") return transformStaffData;
    if (location.pathname === "/plan-management") return transformPlanData;
    if (location.pathname === "/vehicle-management")
      return transformVehicleData;
    return undefined;
  };

  const getExportAllDataTransformer = () => {
    if (location.pathname === "/support") return transformSupportDataAll;
    if (location.pathname === "/booking-management")
      return transformBookingDataAll;
    if (location.pathname === "/ride-management") return transformRideDataAll;
    if (location.pathname === "/payment-management")
      return transformPaymentDataAll;
    if (location.pathname === "/hub-management") return transformHubDataAll;
    if (location.pathname === "/staff-management") return transformStaffDataAll;
    if (location.pathname === "/plan-management") return transformPlanDataAll;
    if (location.pathname === "/vehicle-management")
      return transformVehicleDataAll;
    return undefined;
  };

  const transformPaymentData = (data: any[]) => {
    const rows = Array.isArray(data)
      ? data
      : ((data as any)?.data ?? (data as any)?.result ?? []);
    const filteredData = filterPaymentExportRowsBySavedHub(rows);

    const formatDateForExcel = (
      dateString: string | null | undefined,
    ): string => {
      if (!dateString) return "N/A";
      try {
        const date = convertTimestampToIST(dateString);
        if (!date) return "N/A";
        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch {
        return "N/A";
      }
    };

    return filteredData.map((p: any) => ({
      "Payment ID": p.paymentid ?? "N/A",
      "Booking ID": p.bookingid ?? "N/A",
      "User Name": p.username ?? "N/A",
      "Mobile Number": p.mobileno ?? "N/A",
      "Hub Name": p.hubname ?? "N/A",
      Amount: p.amount ?? "N/A",
      "Discounted Amount": formatPaymentDiscountedAmountForExport(p),
      "Payment Method": ((): string => {
        const method = p.paymentmethod;
        if (method === 1) return "Card";
        if (method === 2) return "UPI";
        if (method === 3) return "Wallet";
        if (method === 4) return "Net Banking";
        return String(method ?? "N/A");
      })(),
      "Transaction ID": p.transactionid ?? "N/A",
      "Summary of Payments": p.summeryofpayments ?? "N/A",
      "Payment Date": formatDateForExcel(p.enteredon),
    }));
  };

  const getExportFilename = () => {
    switch (location.pathname) {
      case "/staff-management":
        return "staff-export.csv";
      case "/booking-management":
        return "bookings-export.xlsx";
      case "/plan-management":
        return "plans-export.csv";
      case "/hub-management":
        return "hubs-export.csv";
      case "/vehicle-management":
        return "vehicles-export.csv";
      case "/support":
        return "support-tickets-export.csv";
      case "/payment-management":
        return "Payment_Details.csv";
      case "/ride-management":
        return "rides-export.csv";
      default:
        return "export.csv";
    }
  };

  const handleCreateButtonClick = () => {
    switch (location.pathname) {
      case "/staff-management":
        navigate("/staff-management/create-staff");
        break;
      case "/hub-management":
        navigate("/hub-management/create-hub");
        break;
      case "/vehicle-management":
        navigate("/vehicle-management/create-vehicle");
        break;
      case "/plan-management":
        navigate("/plan-management/create-plan");
        break;
      case "/booking-management":
        navigate("/booking-management/create-booking", {
          state:
            location.state && typeof location.state === "object"
              ? location.state
              : undefined,
        });
        break;
      case "/marketing/promo-codes":
        navigate("/marketing/promo-codes/create");
        break;
      default:
        break;
    }
  };

  const { CreateButton: HeaderCreateButton } = useCreateButton({
    label: getCreateButtonLabel(),
    onClick: handleCreateButtonClick,
  });

  return (
    <AppBar
      position="fixed"
      className="bg-white border-bottom"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.text.primary,
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.0)", // keep same subtle shadow
      }}
    >
      <Toolbar className="d-flex justify-content-between w-100">
        {/* Mobile Menu + Logo + Brand */}
        <Stack direction="row" alignItems="center" spacing={2} className="me-3">
          {isMobile && (
            <IconButton
              aria-label="open drawer"
              onClick={onMenuClick}
              sx={{
                backgroundColor: "rgba(22, 67, 135, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(22, 67, 135, 0.15)",
                },
                mr: 1,
              }}
            >
              <MenuIcon sx={{ color: "rgba(22, 67, 135, 1)" }} />
            </IconButton>
          )}
          <Box
            onClick={() => navigate("/dashboard")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <CustomLogo width={32} height={24} />
            <img
              src="/Nimbo.svg"
              alt="Nimbo"
              style={{ height: "30px", width: "auto" }}
            />
          </Box>
        </Stack>

        {/* Title / Breadcrumb */}
        <Box
          className="d-flex align-items-center flex-grow-1"
          sx={{ ml: "5rem" }}
        >
          {renderBreadcrumb(
            "/user-management/user-details",
            "UserManagement",
            currentUserName || "User Details",
            "/user-management",
          )}
          {(() => {
            const params = new URLSearchParams(location.search);
            const userName = params.get("userName") || "Details";
            return renderBreadcrumb(
              "/booking-management/details",
              "Booking Management",
              userName,
              "/booking-management",
            );
          })()}
          {renderBreadcrumb(
            "/booking-management/edit-booking",
            "Booking Management",
            "Edit Booking",
            "/booking-management",
          )}
          {renderBreadcrumb(
            "/booking-management/create-booking",
            "Booking Management",
            "Create Booking",
            "/booking-management",
          )}
          {(() => {
            const params = new URLSearchParams(location.search);
            const userName = params.get("userName") || "Details";
            return renderBreadcrumb(
              "/support/details",
              "Support System",
              userName,
              "/support",
            );
          })()}
          {renderBreadcrumb(
            "/payment-management/details",
            "Payment Management",
            "Payment Details",
            "/payment-management",
          )}
          {renderBreadcrumb(
            "/marketing/promo-codes/create",
            "Marketing",
            promoCodesCreateIsEdit ? "Edit Promo Code" : "Create New Promocode",
            "/marketing/promo-codes",
          )}

          {![
            "/user-management/user-details",
            "/booking-management/details",
            "/booking-management/edit-booking",
            "/booking-management/create-booking",
            "/marketing/promo-codes/create",
            "/support/details",
          ].includes(location.pathname) && (
            <Stack direction="row" alignItems="center" spacing={2}>
              {(location.pathname.includes("/create-") ||
                location.pathname.includes("/edit-") ||
                location.pathname.includes("/staff-details") ||
                location.pathname.includes("/hub-details") ||
                location.pathname.includes("/edit-vehicle") ||
                location.pathname.includes("/vehicle-details") ||
                location.pathname.includes("/plan-details") ||
                location.pathname.includes("/payment-management/details") ||
                location.pathname.includes("/ride-details") ||
                location.pathname.includes(
                  "/user-management/user-details",
                )) && (
                <Button
                  variant="text"
                  className="p-0 me-3"
                  onClick={() => {
                    let basePath;
                    if (location.pathname.includes("/create-")) {
                      basePath = location.pathname.split("/create-")[0];
                    } else if (location.pathname.includes("/edit-")) {
                      basePath = location.pathname.split("/edit-")[0];
                    } else if (location.pathname.includes("/staff-details")) {
                      basePath = location.pathname.split("/staff-details")[0];
                    } else if (location.pathname.includes("/hub-details")) {
                      basePath = location.pathname.split("/hub-details")[0];
                    } else if (location.pathname.includes("/edit-vehicle")) {
                      basePath = location.pathname.split("/edit-vehicle")[0];
                    } else if (location.pathname.includes("/vehicle-details")) {
                      basePath = location.pathname.split("/vehicle-details")[0];
                    } else if (location.pathname.includes("/plan-details")) {
                      basePath = location.pathname.split("/plan-details")[0];
                    } else if (
                      location.pathname.includes("/payment-management/details")
                    ) {
                      basePath = "/payment-management";
                    } else if (location.pathname.includes("/ride-details")) {
                      basePath = "/ride-management";
                    } else if (
                      location.pathname.includes(
                        "/user-management/user-details",
                      )
                    ) {
                      basePath = "/user-management";
                    }
                    if (basePath) {
                      if (
                        location.pathname.includes(
                          "/payment-management/details",
                        ) ||
                        location.pathname.includes("/ride-details") ||
                        location.pathname.includes(
                          "/user-management/user-details",
                        ) ||
                        basePath === "/support" ||
                        basePath === "/staff-management" ||
                        basePath === "/hub-management" ||
                        basePath === "/vehicle-management" ||
                        basePath === "/plan-management" ||
                        basePath === "/user-management"
                      ) {
                        navigate(basePath, { state: location.state });
                      } else {
                        navigate(basePath);
                      }
                    }
                  }}
                  sx={{ minWidth: "auto" }}
                >
                  <img src={BackIcon} alt="Back" width={30} height={30} />
                </Button>
              )}
              <Typography
                variant="h6"
                noWrap
                className="fw-bold"
                sx={{ fontSize: "1.5rem" }}
              >
                {renderPageTitle()}
              </Typography>
              {/* Hub switcher for multi-hub roles - placed after page title */}
              {isMultiHub && multiHubOptions.length > 0 && (
                <Select
                  size="small"
                  value={currentHubId ?? ""}
                  onChange={(e) => {
                    const hubId = Number(e.target.value);
                    const hub = multiHubOptions.find(
                      (h) => h.hubmasterid === hubId,
                    );
                    if (hub) {
                      setHubId(hubId);
                      setHubName(hub.hubname || "");
                      navigate(location.pathname, {
                        replace: true,
                        state: {
                          ...location.state,
                          refreshHubs: true,
                          refresh: true,
                          timestamp: Date.now(),
                        },
                      });
                    }
                  }}
                  displayEmpty
                  sx={{
                    ml: 3,
                    minWidth: 200,
                    maxWidth: 250,
                    height: 40,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    border: "1px solid rgba(209, 213, 219, 1)",
                    borderRadius: "6px",
                    "& .MuiSelect-select": {
                      py: 1.25,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 600,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(243, 244, 246, 1)",
                      border: "1px solid rgba(22, 67, 135, 0.4)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      border: "1px solid rgba(22, 67, 135, 1)",
                      boxShadow: "0 0 0 3px rgba(22, 67, 135, 0.1)",
                    },
                  }}
                >
                  {multiHubOptions.map((hub) => (
                    <MenuItem
                      key={hub.hubmasterid}
                      value={hub.hubmasterid}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        py: 1.5,
                        "&:hover": {
                          backgroundColor: "rgba(22, 67, 135, 0.08)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(22, 67, 135, 0.12)",
                          "&:hover": {
                            backgroundColor: "rgba(22, 67, 135, 0.16)",
                          },
                        },
                      }}
                    >
                      {hub.hubname || `Hub ${hub.hubmasterid}`}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Stack>
          )}
        </Box>

        {/* Right section (buttons) */}
        <Stack direction="row" alignItems="center" spacing={2}>
          {location.pathname === "/vehicle-management" &&
            user?.roleid &&
            [1, 2, 7, 10].includes(user.roleid) && (
              <Button
                variant="outlined"
                onClick={() => setShowBulkUploadVehicle(true)}
                startIcon={<FileUploadIcon />}
                sx={{
                  color: "rgba(22, 67, 135, 1)",
                  borderColor: "rgba(22, 67, 135, 1)",
                  "&:hover": {
                    backgroundColor: "rgba(22, 67, 135, 0.04)",
                    borderColor: "rgba(22, 67, 135, 1)",
                  },
                }}
              >
                Bulk Upload
              </Button>
            )}
          {shouldShowCreateButton() && <HeaderCreateButton />}
          {shouldShowExportButton() && (
            <CsvExport
              apiEndpoint={getExportApiEndpoint()}
              apiPayload={() => getExportApiPayload(false)}
              filename={getExportFilename()}
              method={
                location.pathname === "/booking-management" ||
                location.pathname === "/payment-management" ||
                location.pathname === "/staff-management" ||
                location.pathname === "/ride-management" ||
                location.pathname === "/support" ||
                location.pathname === "/vehicle-management"
                  ? "post"
                  : "get"
              }
              customApiCall={getExportCustomApiCall(false)}
              dataTransformer={getExportDataTransformer()}
              buttonLabel={
                location.pathname === "/booking-management"
                  ? "Export Excel"
                  : "Export CSV"
              }
            />
          )}
          {headerAfterExportSlot}
          {shouldShowExportAllButton() && (
            <CsvExport
              apiEndpoint={getExportApiEndpoint()}
              apiPayload={() => getExportApiPayload(true)}
              filename={getExportFilename()
                .replace(".csv", "-all.csv")
                .replace(".xlsx", "-all.xlsx")}
              method={
                location.pathname === "/booking-management" ||
                location.pathname === "/payment-management" ||
                location.pathname === "/staff-management" ||
                location.pathname === "/ride-management" ||
                location.pathname === "/support" ||
                location.pathname === "/vehicle-management"
                  ? "post"
                  : "get"
              }
              customApiCall={getExportCustomApiCall(true)}
              dataTransformer={getExportAllDataTransformer()}
              buttonLabel={
                location.pathname === "/booking-management"
                  ? "Export All Excel"
                  : "Export All CSV"
              }
            />
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
